import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { Text } from '@rbilabs/universal-components';
import { StyleSheet, View } from 'react-native';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  ScrollViewProps,
} from 'react-native';

import { StaticImage, StaticImages } from 'components/picture/static-image';
import RefreshControl from 'components/pull-to-reload/refresh-control';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { isIos, isNative } from 'utils/environment';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';

/** can be passed into a ListView to enable more data loading at end of scrolling  */
export const useLoadMoreAtBottom = (dataLoader: () => void) => {
  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
      const bottomOffset = Platform.select({ android: 100, ios: 0, web: 0 });

      if (
        layoutMeasurement.height + contentOffset.y >=
        contentSize.height - (bottomOffset as number)
      ) {
        dataLoader();
      }
    },
    [dataLoader]
  );
  return { onScroll };
};

export interface IPullToReloadOptions {
  onRefresh(): Promise<any>;
  isDisabled?: boolean;
  /** increase this if you can see the refresh control on the page */
  progressViewOffset?: number;
}

export interface IPullToReloadResult {
  pullToReloadSpinner: JSX.Element;
  pullToReloadProps: {
    refreshControl: JSX.Element;
    onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
    onScrollBeginDrag: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
    onScrollEndDrag: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  };
}

export const usePullToReload = ({
  onRefresh,
  isDisabled = false,
  progressViewOffset,
}: IPullToReloadOptions): IPullToReloadResult => {
  const [isDragging, setIsDragging] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPullingDown, setIsPullingDown] = useState(false);
  const [hidePullMessage, setHidePullMessage] = useState(false);

  // calls original refresh function but makes sure it sticks around for loading for 1s to give user feedback
  const wrappedOnRefresh = useCallback(() => {
    setIsRefreshing(true);
    hapticNotification({ type: HapticsNotificationType.SUCCESS });

    // for good loading indicator UX and prove to user something happened
    const minDuration = new Promise<void>(resolve => setTimeout(resolve, 750));

    // returning promise for react-native-web-refresh-control, react native api doesn't use.
    return Promise.all([onRefresh(), minDuration]).then(() => {
      setIsRefreshing(false);
      setHidePullMessage(true);
      setIsPullingDown(false);
      setTimeout(() => setHidePullMessage(false), 1000); // the built in animation time for ios to contract refresh loader
    });
  }, [onRefresh]);

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!isDragging) {
        setIsPullingDown(false);
        return;
      }

      const offsetY = event.nativeEvent.contentOffset.y;

      // Consider any overscroll at top as a pull gesture; also clear when scrolled back
      if (offsetY <= -30) {
        setIsPullingDown(true);
      } else {
        setIsPullingDown(false);
      }
    },
    [isDragging]
  );

  const onScrollBeginDrag = useCallback(() => {
    setIsDragging(true);
  }, []);

  const onScrollEndDrag = useCallback(() => {
    setIsDragging(false);
  }, []);

  const isMobile = useIsMobileBreakpoint();

  const message = t('pullToRefresh');

  const pullToReloadSpinner = useMemo(() => {
    if (hidePullMessage || !isIos) {
      return null;
    }
    return (
      <View style={[styles.container, { minHeight: isRefreshing ? 50 : 0 }]}>
        {!isRefreshing && isPullingDown && (
          <>
            <Text fontWeight="bold" textAlign="center">
              {message}
            </Text>
            <StaticImage alt={message} source={StaticImages.PullDownToReload} />
          </>
        )}
        {isRefreshing && <StaticImage alt={message} source={StaticImages.PullDownReloading} />}
      </View>
    );
  }, [hidePullMessage, isPullingDown, isRefreshing, message]);

  if (!isMobile || isDisabled) {
    return { pullToReloadProps: {} as any } as IPullToReloadResult;
  }

  const refreshControl = (
    <RefreshControl
      refreshing={isRefreshing}
      onRefresh={wrappedOnRefresh}
      // When using a custom overlay, hide the native spinner (iOS)
      {...(isNative
        ? {
            progressViewOffset,
            tintColor: 'transparent',
          }
        : {})}
    />
  );

  return {
    pullToReloadSpinner,
    pullToReloadProps: {
      refreshControl,
      onScroll,
      onScrollBeginDrag,
      onScrollEndDrag,
    },
  };
};

/**
 * - ✅: if you don't already have a ScrollView / FlatList and you need "Pull to reload"
 * - ❌: If you just need pull to reload, use the usePullToReload hook instead
 */
export const PullToReloadScrollView: FC<React.PropsWithChildren<
  ScrollViewProps & IPullToReloadOptions
>> = ({ onRefresh, isDisabled, progressViewOffset, ...props }) => {
  const scrollViewRef = useRef<ScrollView>(null);

  const {
    pullToReloadProps: { refreshControl, onScroll, onScrollBeginDrag, onScrollEndDrag },
    pullToReloadSpinner,
  } = usePullToReload({
    isDisabled,
    onRefresh,
    progressViewOffset,
  });
  return (
    <View style={pullToReloadStyles.scrollViewContainer}>
      {pullToReloadSpinner}
      <ScrollView
        ref={scrollViewRef}
        nestedScrollEnabled
        showsVerticalScrollIndicator={false}
        refreshControl={refreshControl}
        scrollEventThrottle={16}
        onScrollBeginDrag={event => {
          onScrollBeginDrag?.(event);
          props.onScrollBeginDrag?.(event);
        }}
        onScrollEndDrag={event => {
          onScrollEndDrag?.(event);
          props.onScrollEndDrag?.(event);
        }}
        onScroll={event => {
          onScroll?.(event);
          props.onScroll?.(event);
        }}
        {...props}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    paddingTop: 10,
    left: 0,
    right: 0,
    zIndex: 1,
    pointerEvents: 'none',
  },
});

export const pullToReloadStyles = StyleSheet.create({
  scrollViewContainer: {
    flex: 1,
    position: 'relative',
  },
});
